/**
 * The Fathom side of `track()`. Nothing else should import this: `track()`
 * decides the Fathom name from the event registry, so calling Fathom directly
 * would bypass the registry and the `user_events` table.
 *
 * Events are only delivered in production, where the Fathom script is loaded.
 */

/*
 * The console is this file's only output. Tracking fails silently by design: an
 * event that never fires reads as zero rather than as an error. These lines are
 * how you tell "not fired" from "fired and dropped" while developing, and they
 * are debug level, so production consoles stay quiet.
 */
/* eslint-disable no-console */

interface FathomEventOptions {
  /** Monetary value in cents (e.g., 1999 = $19.99) */
  _value?: number;
}

interface FathomGlobal {
  trackEvent: (eventName: string, options?: FathomEventOptions) => void;
}

declare global {
  interface Window {
    fathom?: FathomGlobal;
  }
}

// Events fired before the Fathom script has loaded
const eventQueue: Array<{ name: string; valueCents?: number }> = [];
let fathomReady = false;

const deliver = (name: string, valueCents?: number): void => {
  if (typeof window === 'undefined' || !window.fathom) {
    console.debug(`[Fathom] Event not tracked (dev mode): ${name}`, valueCents);
    return;
  }

  try {
    const options: FathomEventOptions = {};

    if (valueCents !== undefined) {
      options._value = valueCents;
    }

    window.fathom.trackEvent(name, options);
    console.debug(`[Fathom] Event tracked: ${name}`, options);
  } catch (error) {
    console.error(`[Fathom] Failed to track event: ${name}`, error);
  }
};

/**
 * Waits for the Fathom script, then flushes anything queued behind it. Checks
 * every 100ms; a queued event dies if the page navigates away first, which is
 * why stored events go through `user_events` with `keepalive` instead.
 */
const initializeFathomQueue = (() => {
  let initialized = false;

  return () => {
    if (initialized || typeof window === 'undefined') {
      return;
    }
    initialized = true;

    const checkFathom = () => {
      if (window.fathom) {
        fathomReady = true;
        console.debug(`[Fathom] Ready, flushing ${eventQueue.length} queued events`);

        while (eventQueue.length > 0) {
          const event = eventQueue.shift();
          if (event) {
            deliver(event.name, event.valueCents);
          }
        }
      } else {
        setTimeout(checkFathom, 100);
      }
    };

    setTimeout(checkFathom, 0);
  };
})();

export const sendToFathom = (name: string, valueCents?: number): void => {
  if (typeof window === 'undefined') {
    return;
  }

  initializeFathomQueue();

  if (fathomReady && window.fathom) {
    deliver(name, valueCents);
  } else {
    console.debug(`[Fathom] Queueing event (Fathom not ready yet): ${name}`);
    eventQueue.push({ name, valueCents });
  }
};
